import {Component, OnDestroy, Output, EventEmitter, QueryList, ViewChildren, Input} from "@angular/core";
import { FormControl } from "@angular/forms";
import {
    TCI_CONNECTION_ACTION_TYPE,
    ITCITenantAppCategory,
    ITCIConnector,
    ITCIConnectionServiceProvider,
    ITCIConnectionResponse,
    TCITenantAppCategory,
    TCIConnectionServiceNotificationType
} from "@tci/common";
import { PerfectScrollbarDirective as ScrollbarDirective } from "ngx-perfect-scrollbar";

import { NgToastrService } from "../../ng-shared/ng-components/ng-toastr/ng-toastr.service";
import { NgConnectionsService } from "../ng-connections.service";
import { TciTenantService } from "../../ng-tenant/tci-tenant.service";
import { TciTenantModelService } from "../../ng-tenant/tci-tenant.model";

import { ICategoryFilterOptions, getConnectionFilterOptions } from "../../ng-shared/ng-util/tools";
import { Subscription, Observable } from "rxjs";
import * as lodash from "lodash";

const FILTER_LABEL_ALL = "All";
const CONN_SELECT_SESSION_ITEM = "TCI-CONN-SELECT-SESSION-DATA";


interface IStoredFilter {
    categoryId: string;
    name: string;
}

@Component({
    selector: "ng-connections-select",
    templateUrl: "./ng-connections-select.component.html",
    styleUrls: ["./ng-connections-select.component.less"]
})
export class NgConnectionsSelectComponent implements OnDestroy {
    @Input() showCategory: boolean;
    @Output() Close: EventEmitter<null> = new EventEmitter();
    @Output() Refresh: EventEmitter<null> = new EventEmitter();
    @ViewChildren(ScrollbarDirective) scrollbars: QueryList<ScrollbarDirective>;

    private loading = true;
    private isCreating = false;
    private connectors: ITCIConnector[] = [];
    private selected: ITCIConnector;

    // // Filter handlers
    private FILTER_CATEGORIES: ICategoryFilterOptions[] = [];
    private filteredConns: ITCIConnector[] = [];
    private categoryFilter: string = FILTER_LABEL_ALL;
    private nameFilterCtrl = new FormControl();
    private isConnectors = false;

    // Subscriptions collection
    private subscriptions: Subscription[] = [];

    // Allowance flags
    private canCreate: { [key: string]: boolean } = {};

    constructor(
        private connSrv: NgConnectionsService,
        private toastrSvc: NgToastrService,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService
    ) {
        this.subscriptions.push(this.nameFilterCtrl.valueChanges
            .debounceTime(300)
            .subscribe(() => this.filterConns()));

        connSrv.getConnectors().subscribe(
            conns => {
                this.connectors = conns;
                this.isConnectors = true;
                conns.forEach(conn => {
                    this.canCreate[conn.id] = false;
                    this.checkCanCreate(conn).subscribe(res => this.canCreate[conn.id] = res);
                });

                this.generateLeftSideFilters()
                    .restoreFilter()
                    .filterConns();
            },
            () => this.loading = false
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    cancel() {
        this.Close.emit();
    }

    /**
     * <List filtering instructions>
     */
    private generateLeftSideFilters() {
        this.FILTER_CATEGORIES = getConnectionFilterOptions(FILTER_LABEL_ALL)
            .map(filterOpt => {
                if (filterOpt.category.id === FILTER_LABEL_ALL) {
                    filterOpt.counter = this.connectors.length;
                } else {
                    filterOpt.counter = this.connectors
                        .filter(connector => TCITenantAppCategory.fromConnector(connector).id === filterOpt.category.id)
                        .length;
                }
                return filterOpt;
            })
            .filter(opt => opt.counter > 0);
        return this;
    }

    private filterConns() {
        const filterMakeup = (f: string) => f.toString().toLowerCase().replace(/ /ig, "").replace(/_/, "").trim();
        const nameFilter = this.nameFilterCtrl.value || "";
        this.filteredConns = this.connectors
            .filter(conn =>
                this.categoryFilter === FILTER_LABEL_ALL ||
                TCITenantAppCategory.fromConnector(conn).id === this.categoryFilter
            )
            .filter(conn => nameFilter === "" || filterMakeup(conn.name).indexOf(filterMakeup(nameFilter)) >= 0);
        this.storeConnSelectStage();
    }

    private filterByCategory(opt: ITCITenantAppCategory) {
        const filteredCategory: string = opt.id === FILTER_LABEL_ALL ? FILTER_LABEL_ALL : opt.id;
        if (this.categoryFilter !== filteredCategory) {
            this.categoryFilter = filteredCategory;
            this.filterConns();
        }
    }

    private resetFilters() {
        this.categoryFilter = FILTER_LABEL_ALL;
        this.nameFilterCtrl.setValue("");
        this.filterConns();
    }

    /**
     * </List filtering instructions>
     *
     * <Session Storage instructions>
     */
    storeConnSelectStage() {
        const filters: IStoredFilter = {
            name: this.nameFilterCtrl.value || "",
            categoryId: this.categoryFilter
        };
        sessionStorage.setItem(CONN_SELECT_SESSION_ITEM, JSON.stringify(filters));
    }

    restoreFilter() {
        const filterData = sessionStorage.getItem(CONN_SELECT_SESSION_ITEM);
        if (!lodash.isEmpty(filterData)) {
            try {
                const filterStage: IStoredFilter = JSON.parse(filterData);

                if (this.FILTER_CATEGORIES.map(fl => fl.category.id).includes(filterStage.categoryId)) {
                    this.categoryFilter = filterStage.categoryId;
                } else {
                    this.categoryFilter = FILTER_LABEL_ALL;
                    this.storeConnSelectStage();
                }

                if (!lodash.isEmpty(filterStage.name)) { // Restore filter by name
                    this.nameFilterCtrl.setValue(filterStage.name);
                }
            } catch (e) {
                console.error("Connector Selector filter stage restore incomplete", e);
                this.resetFilters();
            }
        } else {
            this.resetFilters();
        }
        return this;
    }

    /**
     * </Session Storage instructions>
     *
     * <UI tools>
     */

    getIcon(conn: ITCIConnector): string {
        return conn.smallIcon ? conn.smallIcon : conn.largeIcon;
    }

    getCategory(conn: ITCIConnector): string {
        return TCITenantAppCategory.fromConnector(conn).label;
    }

    // Create
    getProvider(conn: ITCIConnector): Observable<ITCIConnectionServiceProvider> {
        return this.tenantSvc.onInit()
            .map(() => this.tenantModelSvc.getTenant(conn.tenantType).getConnectionServiceProvider())
            .filter(provider => !!provider);
    }

    checkCanCreate(conn: ITCIConnector): Observable<boolean> {
        return this.getProvider(conn)
            .flatMap(provider => provider.canCreate({ connector: conn, actionType: TCI_CONNECTION_ACTION_TYPE.CONN_ADD }));
    }

    create(conn: ITCIConnector) {
        if (this.canCreate[conn.id]) {
            this.isCreating = true;
            return this.getProvider(conn)
                .flatMap(provider => provider.createConnection({ connector: conn, actionType: TCI_CONNECTION_ACTION_TYPE.CONN_ADD }, "connections"))
                .catch(err => {
                    console.error("Connection Create Error", conn, err);
                    return Observable.of(null);
                })
                .filter(createRes => !!createRes)
                .do(() => this.cancel())
                .subscribe((createRes: ITCIConnectionResponse) => {
                    if (createRes.error) {
                        this.toastrSvc.error("The connection was not able to be created", "for " + conn.name);
                        console.warn("Connection Create returned error", createRes);
                    } else if (createRes.notificationType === TCIConnectionServiceNotificationType.NOTIFICATION_REFRESH ||
                        createRes.notificationType === TCIConnectionServiceNotificationType.NOTIFICATION_CONNECTION_ADDED) {
                        this.Refresh.emit();
                    }
                });
        }
    }
}
